import { Button, InputField, Modal, Textarea } from '@hakimo-ui/shared/ui-base';
import { EmailAttachmentsField } from './EmailAttachmentsField';
import { EmailRecipientsField } from './EmailRecipientsField';
import { useEmailComposeState } from './useEmailComposeState';

export interface EmailPayload {
  to: string[];
  bcc: string[];
  subject: string;
  body: string;
  attachments: File[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSend: (payload: EmailPayload) => void;
  isSending: boolean;
  locationId: number | null;
  site: string | null;
  incidentStartTime: string;
  description: string;
  subjectId?: string | number | null;
  initialAttachment?: File | null;
  onPreviewAttachment: (file: File) => void;
  resetOnOpen?: boolean;
}

export const EmailComposeModal = ({
  open,
  onClose,
  onSend,
  isSending,
  locationId,
  site,
  incidentStartTime,
  description,
  subjectId,
  initialAttachment,
  onPreviewAttachment,
  resetOnOpen = true,
}: Props) => {
  const {
    toEmails,
    setToEmails,
    bccEmails,
    setBccEmails,
    subject,
    setSubject,
    body,
    setBody,
    attachments,
    setAttachments,
    reset,
  } = useEmailComposeState({
    open,
    locationId,
    site,
    incidentStartTime,
    description,
    subjectId,
    initialAttachment,
    resetOnOpen,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const send = () =>
    onSend({ to: toEmails, bcc: bccEmails, subject, body, attachments });
  const cannotSend =
    isSending || toEmails.length === 0 || attachments.length === 0;

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={send}
        loading={isSending}
        disabled={cannotSend}
      >
        Send
      </Button>
      <Button onClick={handleClose} disabled={isSending}>
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Send Incident Report"
      footer={actions}
      closable={!isSending}
      disableBackdropClose
    >
      <div className="w-[600px] max-w-full space-y-4 p-6">
        <EmailRecipientsField
          label="To"
          emails={toEmails}
          onChange={setToEmails}
        />

        <EmailRecipientsField
          label="BCC"
          emails={bccEmails}
          onChange={setBccEmails}
          placeholder="Enter BCC email addresses"
        />

        <InputField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          type="text"
          placeholder="Enter email subject..."
        />

        <Textarea
          label="Body"
          value={body}
          onChange={setBody}
          rows={12}
          minRows={6}
          maxRows={16}
          placeholder="Enter email body..."
        />

        <EmailAttachmentsField
          files={attachments}
          onChange={setAttachments}
          onPreview={onPreviewAttachment}
        />
      </div>
    </Modal>
  );
};

export default EmailComposeModal;
